import React, { useState } from 'react';
import axios from 'axios';
import {
    Paper,
    Container,
    Typography,
    Box,
    Grid,
    CircularProgress,
    Snackbar,
    Alert,
    Card,
    CardContent,
    Table,
    Button,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import StyledButton from '../layout/StyledButton';
import AnimatedText from '../layout/AnimatedText';
import flaskServer from '../config/flaskserver';
import RelatorioComponente from './RelatorioComponente';

const ConciliacaoTiss = () => {
    const navigate = useNavigate();
    const [xmlFile, setXmlFile] = useState(null);
    const [priceTable, setPriceTable] = useState(null);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [summary, setSummary] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [convenios, setConvenios] = useState([]);
    const [convenioId, setConvenioId] = useState('');

    React.useEffect(() => {
        fetchConvenios();
    }, []);

    const fetchConvenios = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/convenios`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setConvenios(response.data.convenios || []);
        } catch (error) {
            console.error('Erro ao buscar convênios:', error);
        }
    };

    const handleXmlChange = (e) => setXmlFile(e.target.files[0]);
    const handlePriceTableChange = (e) => setPriceTable(e.target.files[0]);

    const handleProcess = async () => {
        if (!xmlFile) {
            setSnackbar({ open: true, message: 'Por favor, selecione o arquivo TISS (XML ou ZIP).', severity: 'warning' });
            return;
        }

        if (!priceTable && !convenioId) {
            setSnackbar({ open: true, message: 'Selecione um convênio ou envie uma tabela de preços.', severity: 'warning' });
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append('file', xmlFile);
        if (priceTable) formData.append('price_table', priceTable);
        if (convenioId) formData.append('convenio_id', convenioId);

        const token = localStorage.getItem('token');

        try {
            const response = await axios.post(`${flaskServer.flaskServerIP}/conciliacao-tiss`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.status === 'success') {
                setResults(response.data.data);
                setSummary(response.data.summary);
                setSnackbar({ open: true, message: 'Conciliação finalizada com sucesso!', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: response.data.message, severity: 'error' });
            }
        } catch (error) {
            console.error('Error:', error);
            setSnackbar({ open: true, message: 'Erro ao processar a conciliação.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Conciliado': return 'success';
            case 'Divergente': return 'error';
            case 'Não Encontrado': return 'warning';
            default: return 'default';
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => navigate('/conciliar')} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                    <AnimatedText text="Conciliação de Faturamento TISS" />
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* File Upload Section */}
                <Grid item xs={12} lg={4}>
                    <Paper elevation={3} sx={{
                        p: 3,
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                    }}>
                        <Typography variant="h6" gutterBottom>Upload de Arquivos</Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>Arquivo TISS XML ou ZIP (Lotes)</Typography>
                            <Button
                                component="label"
                                variant="outlined"
                                fullWidth
                                startIcon={<CloudUploadIcon />}
                                sx={{ height: 60, borderStyle: 'dashed' }}
                            >
                                {xmlFile ? xmlFile.name : 'Selecionar XML/ZIP'}
                                <input type="file" hidden accept=".xml,.zip" onChange={handleXmlChange} />
                            </Button>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>Selecionar Convênio Cadastrado</Typography>
                            <select
                                value={convenioId}
                                onChange={(e) => setConvenioId(e.target.value)}
                                style={{
                                    width: '100%',
                                    height: '50px',
                                    borderRadius: '8px',
                                    padding: '0 10px',
                                    border: '1px solid rgba(0,0,0,0.23)',
                                    background: 'transparent'
                                }}
                            >
                                <option value="">-- Usar Tabela Manual (Upload abaixo) --</option>
                                {convenios.map(c => (
                                    <option key={c.id} value={c.id}>{c.nome}</option>
                                ))}
                            </select>
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>OU Upload Manual de Tabela (Excel/CSV)</Typography>
                            <Button
                                component="label"
                                variant="outlined"
                                fullWidth
                                disabled={!!convenioId}
                                startIcon={<CloudUploadIcon />}
                                sx={{ height: 60, borderStyle: 'dashed', opacity: convenioId ? 0.5 : 1 }}
                            >
                                {priceTable ? priceTable.name : (convenioId ? 'Usando Tabela do Banco' : 'Selecionar Tabela')}
                                <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handlePriceTableChange} />
                            </Button>
                        </Box>

                        <StyledButton
                            fullWidth
                            onClick={handleProcess}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Processar Conciliação'}
                        </StyledButton>
                    </Paper>

                    {summary && (
                        <Card sx={{ mt: 3, borderRadius: 4, background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', color: 'white' }}>
                            <CardContent sx={{ color: 'white' }}>
                                <Typography variant="h6" gutterBottom>Impacto Financeiro</Typography>
                                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(summary.total_impacto)}
                                </Typography>
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                                    <Box textAlign="center">
                                        <Typography variant="caption">Divergências</Typography>
                                        <Typography variant="h6">{summary.items_divergentes}</Typography>
                                    </Box>
                                    <Box textAlign="center">
                                        <Typography variant="caption">Conciliados</Typography>
                                        <Typography variant="h6">{summary.items_conciliados}</Typography>
                                    </Box>
                                    <Box textAlign="center">
                                        <Typography variant="caption">Ausentes</Typography>
                                        <Typography variant="h6">{summary.items_nao_encontrados}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ mt: 3 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={<FileDownloadIcon />}
                                        onClick={() => setExportDialogOpen(true)}
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                            backdropFilter: 'blur(5px)'
                                        }}
                                    >
                                        Exportar Relatório
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Grid>

                {/* Results Section */}
                <Grid item xs={12} lg={8}>
                    <Paper elevation={3} sx={{
                        p: 0,
                        borderRadius: 4,
                        overflow: 'hidden',
                        minHeight: 500,
                        background: 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(10px)',
                    }}>
                        {!results ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 500, color: 'text.secondary' }}>
                                <FilePresentIcon sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                                <Typography>Aguardando processamento dos arquivos...</Typography>
                            </Box>
                        ) : (
                            <TableContainer sx={{ maxHeight: 700 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Data Exec.</TableCell>
                                            <TableCell>Guia Prest.</TableCell>
                                            <TableCell>Guia Oper.</TableCell>
                                            <TableCell>Carteira</TableCell>
                                            <TableCell>Paciente</TableCell>
                                            <TableCell>Início/Fim</TableCell>
                                            <TableCell>Código</TableCell>
                                            <TableCell>Descrição</TableCell>
                                            <TableCell align="right">Qtde</TableCell>
                                            <TableCell align="right">Vlr. Cobrado</TableCell>
                                            <TableCell align="right">Vlr. Tabela</TableCell>
                                            <TableCell align="right">Diferença</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {results.map((item, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>
                                                    <Chip
                                                        label={item.status}
                                                        color={getStatusColor(item.status)}
                                                        size="small"
                                                        sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{item.data}</TableCell>
                                                <TableCell sx={{ fontSize: '0.7rem' }}>{item.guia}</TableCell>
                                                <TableCell sx={{ fontSize: '0.7rem' }}>{item.guia_operadora}</TableCell>
                                                <TableCell sx={{ fontSize: '0.7rem' }}>{item.carteira}</TableCell>
                                                <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                                                    <Tooltip title={item.paciente}>
                                                        <span>{item.paciente}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                                                    {item.data_inicio}<br />{item.data_fim}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.7rem' }}>{item.codigo}</TableCell>
                                                <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                                                    <Tooltip title={item.descricao}>
                                                        <span>{item.descricao}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.7rem' }}>{item.quantidade}</TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.7rem' }}>{formatCurrency(item.valor_unitario_cobrado)}</TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.7rem' }}>{formatCurrency(item.valor_unitario_tabela)}</TableCell>
                                                <TableCell align="right" sx={{
                                                    fontSize: '0.7rem',
                                                    color: item.diferenca > 0 ? 'error.main' : item.diferenca < 0 ? 'success.main' : 'inherit',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {formatCurrency(item.diferenca)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Export Dialog */}
            <Dialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Exportar Relatório de Conciliação TISS
                    <IconButton onClick={() => setExportDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <RelatorioComponente option="tiss" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportDialogOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ConciliacaoTiss;
